import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, CheckCircle2, ListChecks, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatError } from "@/lib/api";
import QuestionImage from "@/components/QuestionImage";
import { gradeSubjects } from "@/lib/subjects";
import QuestionEditor from "@/components/admin/QuestionEditor";

const GRADES = [1, 2, 3, 4, 5, 6];

const LETTERS = ["A", "B", "C", "D"];

export default function ReviewQuestions() {
  const [grade, setGrade] = useState("1");
  const [subject, setSubject] = useState("Maths");
  const [editing, setEditing] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [chapters, setChapters] = useState([]);
  const [chapterId, setChapterId] = useState("");
  const [level, setLevel] = useState("1");
  const [lang, setLang] = useState("en");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    setChapters([]); setChapterId(""); setData(null);
    api.get(`/review/chapters/${grade}/${encodeURIComponent(subject)}`)
      .then(({ data }) => {
        setChapters(data.chapters);
        if (data.chapters.length) setChapterId(data.chapters[0].chapter_id);
      })
      .catch(() => setChapters([]));
  }, [grade, subject]);

  useEffect(() => {
    if (!chapterId) return;
    setLoading(!reloadKey || !data); setLoadError("");
    api.get(`/review/level`, { params: { grade, subject, chapter_id: chapterId, level } })
      .then(({ data }) => setData(data))
      .catch((err) => { setData(null); setLoadError(formatError(err?.response?.data?.detail)); })
      .finally(() => setLoading(false));
  }, [chapterId, level, grade, subject, reloadKey]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-parent text-slate-900">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center gap-3">
          <Link to="/admin">
            <Button variant="ghost" className="rounded-full" data-testid="review-back">
              <ArrowLeft size={18} className="mr-1" /> Admin
            </Button>
          </Link>
          <span className="font-bold flex items-center gap-2"><ListChecks size={18} className="text-blue-600" /> Question review (QA)</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Select value={grade} onValueChange={(g) => { setGrade(g); if (!gradeSubjects(g).includes(subject)) setSubject("Maths"); }}>
            <SelectTrigger data-testid="review-grade"><SelectValue placeholder="Grade" /></SelectTrigger>
            <SelectContent>
              {GRADES.map((g) => <SelectItem key={g} value={String(g)}>Grade {g}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={subject} onValueChange={setSubject}>
            <SelectTrigger data-testid="review-subject"><SelectValue>{subject}</SelectValue></SelectTrigger>
            <SelectContent>
              {gradeSubjects(grade).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={chapterId} onValueChange={setChapterId}>
            <SelectTrigger data-testid="review-chapter"><SelectValue placeholder="Chapter" /></SelectTrigger>
            <SelectContent>
              {chapters.map((c) => <SelectItem key={c.chapter_id} value={c.chapter_id}>{c.index + 1}. {c.title}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger data-testid="review-level"><SelectValue placeholder="Level" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Easy</SelectItem>
              <SelectItem value="2">Medium</SelectItem>
              <SelectItem value="3">Difficult</SelectItem>
            </SelectContent>
          </Select>
          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger data-testid="review-picture-lang"><SelectValue placeholder="Pictures" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="en">Pictures: English</SelectItem>
              <SelectItem value="fr">Pictures: French</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading && (
          <div className="flex flex-col items-center justify-center py-20 text-slate-500">
            <Loader2 className="animate-spin text-blue-600 mb-3" size={32} />
            <p>Loading questions (images generate on first view)…</p>
          </div>
        )}

        {loadError && !loading && (
          <p className="mt-8 text-center text-slate-600 bg-amber-50 border border-amber-200 rounded-xl px-4 py-6" data-testid="review-load-error">{loadError}</p>
        )}

        <QuestionEditor target={editing} onClose={() => setEditing(null)} onSaved={() => setReloadKey((k) => k + 1)} />
        {data && !loading && (
          <>
            <p className="mt-6 text-sm text-slate-500">
              <b>{data.chapter_title}</b> · {data.level_name} · {data.questions.length} questions
            </p>
            <div className="mt-4 space-y-4">
              {data.questions.map((q) => (
                <div key={q.qi} className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5" data-testid={`review-q-${q.qi}`}>
                  <div className="flex gap-4">
                    {q.has_image && (
                      <div className="w-40 shrink-0">
                        <QuestionImage chapterId={chapterId} level={level} qi={q.qi} lang={lang}
                          className="w-40 h-28 object-contain bg-slate-50 rounded-xl border border-slate-200" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2">
                        <p className="font-semibold flex-1"><span className="text-slate-400 mr-1">Q{q.qi + 1}.</span>{q.question}</p>
                        <button onClick={() => setEditing({ chapter_id: chapterId, level: Number(level), qi: q.qi })} data-testid={`review-edit-${q.qi}`}
                          className="shrink-0 inline-flex items-center gap-1 rounded-full bg-blue-600 text-white px-3 py-1 text-xs font-bold"><Pencil size={12} /> Edit</button>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-2 mt-3">
                        {q.options.map((opt, oi) => {
                          const correct = oi === q.answer;
                          return (
                            <div key={oi}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${correct ? "bg-green-50 border-green-300 text-green-800 font-semibold" : "bg-slate-50 border-slate-200 text-slate-600"}`}
                              data-testid={`review-q-${q.qi}-opt-${oi}${correct ? "-correct" : ""}`}>
                              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${correct ? "bg-green-500 text-white" : "bg-slate-200 text-slate-500"}`}>{LETTERS[oi]}</span>
                              <span className="truncate">{opt}</span>
                              {correct && <CheckCircle2 size={16} className="text-green-500 ml-auto shrink-0" />}
                            </div>
                          );
                        })}
                      </div>
                      {q.explanation && (
                        <p className="text-sm text-slate-500 mt-3 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                          💡 {q.explanation}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
